import { useCallback, useEffect, useRef, useState } from 'react';
import {
  speakWithElevenLabs,
  stopElevenLabsAudio,
} from '../services/elevenlabs.js';
import {
  attachAmplitude,
  detachAmplitude,
  setAmplitudeListener,
} from '../services/audioAmplitude.js';

export function useVoice() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [voicesReady] = useState(true); // ElevenLabs is always ready
  const [amplitude, setAmplitude] = useState(0);
  const currentAudioRef = useRef(null);
  const lastAmplitudeRef = useRef(0);

  // Register a persistent amplitude listener so any TTS playback —
  // including direct speakWithElevenLabs calls from speakAndWait — drives
  // the same React state setter, not just useVoice.speak invocations.
  // Throttle updates so React doesn't re-render at 60 Hz; we only push
  // a new value when the smoothed amplitude changes by more than ~0.02,
  // which lands somewhere around 15-20 fps in practice.
  useEffect(() => {
    setAmplitudeListener((amp) => {
      if (Math.abs(amp - lastAmplitudeRef.current) > 0.02) {
        lastAmplitudeRef.current = amp;
        setAmplitude(amp);
      }
    });
    return () => setAmplitudeListener(null);
  }, []);

  const speak = useCallback(async (text, gender = 'female', onEnd) => {
    if (!text) {
      if (onEnd) onEnd();
      return null;
    }

    if (currentAudioRef.current) {
      stopElevenLabsAudio(currentAudioRef.current);
      currentAudioRef.current = null;
      detachAmplitude();
    }

    setIsLoading(true);
    console.log('[useVoice] Speaking:', text.substring(0, 50) + '…');

    const audio = await speakWithElevenLabs(text, gender, () => {
      setIsSpeaking(false);
      setIsLoading(false);
      setAmplitude(0);
      lastAmplitudeRef.current = 0;
      detachAmplitude();
      if (onEnd) onEnd();
    });

    if (audio) {
      currentAudioRef.current = audio;
      attachAmplitude(audio, (amp) => setAmplitude(amp));
      setIsSpeaking(true);
      setIsLoading(false);
    } else {
      // speakWithElevenLabs already invoked onEnd via its catch path
      setIsLoading(false);
    }

    return audio;
  }, []);

  const stop = useCallback(() => {
    if (currentAudioRef.current) {
      stopElevenLabsAudio(currentAudioRef.current);
      currentAudioRef.current = null;
    }
    detachAmplitude();
    setIsSpeaking(false);
    setIsLoading(false);
    setAmplitude(0);
    lastAmplitudeRef.current = 0;
  }, []);

  return { speak, stop, isSpeaking, isLoading, voicesReady, amplitude };
}
